import React, { useState, useEffect, useMemo, useRef } from 'react';
import fileIconMap from '../utils/fileIconMap';
import AdminDangerZone from './Common/AdminDangerZone';
import { FaUserCircle, FaFilter, FaSort, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import { FiChevronRight, FiMoon, FiSearch, FiSun, FiTrash2, FiX } from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import { useNdTheme } from '../utils/ndTheme';
import styled from 'styled-components';
import '../styles/newDesign.css';

// Импорт иконок файлов
import excelIcon from '../assets/icons/excel.png';
import pdfIcon from '../assets/icons/pdf.png';
import wordIcon from '../assets/icons/word.png';
import rarIcon from '../assets/icons/rar.png';
import photoIcon from '../assets/icons/photo.png';

/** Максимум со-исполнителей у одной задачи (как на сервере). */
const MAX_TASK_CO_ASSIGNEES = 10;

function parseCoAssigneeIdsFromTask(raw) {
  if (raw == null || raw === '') return [];
  try {
    const a = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(a)) return [];
    return [...new Set(a.map((x) => parseInt(String(x), 10)).filter((n) => !Number.isNaN(n) && n > 0))];
  } catch {
    return [];
  }
}

// Styled components объявлены вне функции
const Form = styled.form`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  justify-content: flex-start;
  background: var(--modal-tasks-form-background, rgba(44, 62, 80, 0.2));
  border-radius: 12px;
  box-shadow: none;
  padding: 16px;
  border: none;
  width: 100%;
  box-sizing: border-box;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid var(--modal-tasks-border-color, #2c3e50);
  border-radius: 12px;
  background: var(--modal-tasks-input-background, rgba(44, 62, 80, 0.3));
  color: var(
    --modal-tasks-input-text-color,
    var(--modal-tasks-text-color, #fff)
  );
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    border-color: var(--modal-tasks-input-focus-border-color, #6dd5ed);
    background: var(
      --modal-tasks-input-focus-background,
      rgba(44, 62, 80, 0.5)
    );
  }
  
  &::placeholder {
    color: var(
      --modal-tasks-placeholder-color,
      var(--modal-default-label-text-color, #b2bec3)
    );
  }
`;

const Button = styled.button`
  padding: 12px 16px;
  background: var(
    --modal-tasks-button-background,
    linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)
  );
  color: var(--modal-tasks-button-text-color, #fff);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: linear-gradient(135deg, #38f9d7 0%, #43e97b 100%);
    transform: translateY(-2px);
  }
`;

const DeleteBtn = styled.button`
  padding: 8px;
  background: transparent;
  border: none;
  color: #b2bec3;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #e74c3c;
  }
`;

const PrioritySelect = styled.select`
  padding: 12px 16px;
  border: 2px solid var(--modal-tasks-border-color, #2c3e50);
  border-radius: 12px;
  background: var(--modal-tasks-input-background, rgba(44, 62, 80, 0.3));
  color: var(
    --modal-tasks-input-text-color,
    var(--modal-tasks-text-color, #fff)
  );
  font-size: 1rem;
  outline: none;
  cursor: pointer;
  
  &:focus {
    border-color: var(--modal-tasks-input-focus-border-color, #6dd5ed);
  }
  
  option {
    background: #232931;
    color: #fff;
  }
`;

// Компонент ModalBg должен быть объявлен вне основного компонента Tasks
function ModalBg({ children, onClose, lightBackdrop = false }) {
  const overlayBg = lightBackdrop ? 'rgba(15, 23, 42, 0.38)' : 'rgba(34,40,49,0.82)';
  const closeColor = lightBackdrop ? '#64748b' : '#43e97b';
  return (
    <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,zIndex:9999,background:overlayBg,width:'100%',height:'100%',display:'flex',alignItems:'stretch',justifyContent:'center'}} onClick={onClose}>
      <div style={{width:'100%',height:'100%',background:'none',padding:0,margin:0,boxSizing:'border-box',position:'relative',overflow:'auto'}} onClick={e=>e.stopPropagation()}>
        <button type="button" onClick={onClose} style={{position:'absolute',top:18,right:24,fontSize:28,background:'none',border:'none',cursor:'pointer',color:closeColor,zIndex:2}} aria-label="Закрыть">×</button>
        {children}
      </div>
    </div>
  );
}

// Функция для получения иконки файла по расширению
function getFileIcon(filename) {
  if (!filename) return photoIcon;
  
  const ext = filename.split('.').pop()?.toLowerCase();
  
  switch (ext) {
    case 'xlsx':
    case 'xls':
    case 'csv':
      return excelIcon;
    case 'pdf':
      return pdfIcon;
    case 'docx':
    case 'doc':
      return wordIcon;
    case 'zip':
    case 'rar':
    case '7z':
      return rarIcon;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
    case 'svg':
      return photoIcon;
    default:
      return photoIcon;
  }
}

// Основной компонент Tasks
function Tasks({ isMobile = false, modal, onClose, initialFileForCreate, onConsumedInitialFile }) {
  const { state } = useApp();
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [listQuery, setListQuery] = useState('');
  const [viewTaskId, setViewTaskId] = useState(null);
  const [swipeData, setSwipeData] = useState({});
  const [deadlineTimer, setDeadlineTimer] = useState('');
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > window.innerHeight;
    }
    return false;
  });
  const [user, setUser] = useState({});
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignedTo: '',
    coAssigneeIds: [],
    deadline: '',
    priority: 'medium'
  });
  const [files, setFiles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showComplete, setShowComplete] = useState(null);
  const [showExtend, setShowExtend] = useState(null);
  const [comment, setComment] = useState('');
  const [newDeadline, setNewDeadline] = useState('');
  const [commentError, setCommentError] = useState('');
  const [isCompletionRequired, setIsCompletionRequired] = useState(false);
  const [filter, setFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showComment, setShowComment] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [tempDescription, setTempDescription] = useState('');
  const [showDescEmoji, setShowDescEmoji] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchUserId, setSearchUserId] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // Поисковая модалка: combobox для пользователей
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState(0);
  const [searchRole, setSearchRole] = useState('assignee'); // 'assignee' | 'author'
  const filteredSearchUsers = useMemo(() => {
    const simplify = (s) => String(s ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim();
    const q = simplify(searchQuery);
    const qTokens = q.split(/\s+/).filter(Boolean);
    const list = Array.isArray(users) ? users : [];
    if (!qTokens.length) return [];
    return list.filter(u => {
      const first = simplify(u.firstName || u.firstname || u.name || u.first || u.givenName || u.given_name);
      const last = simplify(u.lastName || u.lastname || u.surname || u.last || u.familyName || u.family_name);
      const middle = simplify(u.patronymic || u.middleName || u.middlename || u.middle_name);
      const disp = simplify(u.displayName || u.display || `${first} ${last}`);
      const login = simplify(u.login || u.username);
      const tokens = [first, last, middle, disp, login].filter(Boolean).flatMap(s => s.split(/\s+/));
      // Все токены запроса должны совпасть по началу с любым из токенов пользователя
      return qTokens.every(qt => tokens.some(t => t.startsWith(qt)));
    });
  }, [users, searchQuery]);

  // Фильтрация задач по выбранным параметрам + живые результаты при вводе (строго по префиксу; роль: исполнитель/автор управляется переключателем)
  useEffect(() => {
    const uid = String(searchUserId || '').trim();
    const date = String(searchDate || '').trim();
    const sameDay = (isoA, isoB) => {
      if (!isoA || !isoB) return false;
      const a = new Date(isoA); const b = new Date(isoB);
      return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
    };
    // Если пользователь не выбран явно, но есть ввод, используем совпавших по запросу пользователей (префиксы)
    const matchedIds = (Array.isArray(filteredSearchUsers) ? filteredSearchUsers : []).map(u => String(u.id));
    // Если есть ввод, но совпадений по пользователям нет — возвращаем пустой результат
    if (!uid && searchQuery.trim() && matchedIds.length === 0) {
      setSearchResults([]);
      return;
    }
    const list = Array.isArray(tasks) ? tasks : [];
    const filtered = list.filter(t => {
      const assigneeId = String(t.assignedTo);
      const authorId = String(t.authorId);
      const coIds = parseCoAssigneeIdsFromTask(t.coAssignees).map(String);
      const userMatch = uid
        ? (searchRole === 'assignee'
          ? (assigneeId === uid || coIds.includes(uid))
          : authorId === uid)
        : (searchQuery
          ? (searchRole === 'assignee'
            ? (matchedIds.includes(assigneeId) || matchedIds.some((mid) => coIds.includes(mid)))
            : matchedIds.includes(authorId))
          : true);
      const dateMatch = date ? (t.createdAt && sameDay(t.createdAt, date)) : true;
      return userMatch && dateMatch;
    });
    setSearchResults(filtered);
  }, [tasks, searchUserId, searchDate, searchQuery, filteredSearchUsers, searchRole]);
  // Быстрый поиск исполнителя в модалке создания задачи
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const filteredAssignees = useMemo(() => {
    const simplify = (s) => String(s ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim();
    const qRaw = String(assigneeQuery || '');
    const q = simplify(qRaw);
    const qTokens = q.split(/\s+/).filter(Boolean);
    const list = Array.isArray(users) ? users : [];
    if (!q) return list;
    return list.filter(u => {
      const username = simplify(u.username);
      const login = simplify(u.login);
      const firstName = simplify(u.firstName || u.firstname || u.name || u.first || u.givenName || u.given_name);
      const lastName = simplify(u.lastName || u.lastname || u.surname || u.last || u.familyName || u.family_name);
      const patronymic = simplify(u.patronymic || u.middleName || u.middlename || u.middle_name);
      const fio = simplify(u.fio || `${lastName} ${firstName} ${patronymic}`);
      const fullName = simplify(u.fullName || u.fullname || u.nameRu || `${firstName} ${lastName}`);
      const displayName = simplify(u.displayName || u.display || fio || fullName || `${firstName} ${lastName}`);
      const email = simplify(u.email);
      const phone = simplify(u.phone || u.tel || '');
      const id = simplify(u.id);
      const haystack = [username, login, firstName, lastName, patronymic, fio, fullName, displayName, email, phone, id]
        .filter(Boolean)
        .join(' ');
      // Фолбэк: по всем строковым значениям объекта пользователя (на случай неожиданных полей)
      const allValues = simplify(Object.values(u).join(' '));

      // Все токены из запроса должны встречаться в любой комбинации слов (поддержка ввода первых букв)
      const allTokensMatch = qTokens.every(tok => haystack.includes(tok));
      // Также поддержим быстрый старт по началу имени/фамилии/логина
      const starts = username.startsWith(q) || login.startsWith(q) || firstName.startsWith(q) || lastName.startsWith(q) || displayName.startsWith(q) || fio.startsWith(q) || fullName.startsWith(q);
      return allTokensMatch || starts || allValues.includes(q);
    });
  }, [users, assigneeQuery]);

  // Combobox UI state for assignee
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [assigneeHighlight, setAssigneeHighlight] = useState(0);
  const selectedAssignee = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.find(u => String(u.id) === String(form.assignedTo));
  }, [users, form.assignedTo]);
  const getAssigneeLabel = (u) => {
    if (!u) return '';
    const firstName = u.firstName || u.firstname || '';
    const lastName = u.lastName || u.lastname || '';
    const display = u.displayName || u.display || [firstName, lastName].filter(Boolean).join(' ').trim();
    const login = u.login || u.username || '';
    return (display || login || u.email || String(u.id)).trim();
  };
  const assigneeSelectedLabel = useMemo(() => getAssigneeLabel(selectedAssignee), [selectedAssignee]);

  const [coAssigneeQuery, setCoAssigneeQuery] = useState('');
  const [coAssigneeOpen, setCoAssigneeOpen] = useState(false);
  const [coAssigneeHighlight, setCoAssigneeHighlight] = useState(0);

  useEffect(() => {
    setForm(f => {
      const cur = f.coAssigneeIds || [];
      const filteredCo = cur.filter(id => String(id) !== String(f.assignedTo || ''));
      if (filteredCo.length === cur.length) return f;
      return { ...f, coAssigneeIds: filteredCo };
    });
  }, [form.assignedTo]);

  const filteredCoAssigneePickList = useMemo(() => {
    const simplify = (s) => String(s ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim();
    const qRaw = String(coAssigneeQuery || '');
    const q = simplify(qRaw);
    const qTokens = q.split(/\s+/).filter(Boolean);
    const blocked = new Set(
      [String(form.assignedTo || ''), ...(form.coAssigneeIds || []).map(String)].filter(Boolean)
    );
    const list = (Array.isArray(users) ? users : []).filter(u => !blocked.has(String(u.id)));
    if (!q) return list;
    return list.filter(u => {
      const username = simplify(u.username);
      const login = simplify(u.login);
      const firstName = simplify(u.firstName || u.firstname || u.name || u.first || u.givenName || u.given_name);
      const lastName = simplify(u.lastName || u.lastname || u.surname || u.last || u.familyName || u.family_name);
      const patronymic = simplify(u.patronymic || u.middleName || u.middlename || u.middle_name);
      const fio = simplify(u.fio || `${lastName} ${firstName} ${patronymic}`);
      const fullName = simplify(u.fullName || u.fullname || u.nameRu || `${firstName} ${lastName}`);
      const displayName = simplify(u.displayName || u.display || fio || fullName || `${firstName} ${lastName}`);
      const email = simplify(u.email);
      const phone = simplify(u.phone || u.tel || '');
      const id = simplify(u.id);
      const haystack = [username, login, firstName, lastName, patronymic, fio, fullName, displayName, email, phone, id]
        .filter(Boolean)
        .join(' ');
      const allValues = simplify(Object.values(u).join(' '));
      const allTokensMatch = qTokens.every(tok => haystack.includes(tok));
      const starts = username.startsWith(q) || login.startsWith(q) || firstName.startsWith(q) || lastName.startsWith(q) || displayName.startsWith(q) || fio.startsWith(q) || fullName.startsWith(q);
      return allTokensMatch || starts || allValues.includes(q);
    });
  }, [users, coAssigneeQuery, form.assignedTo, form.coAssigneeIds]);

  const [subtaskForm, setSubtaskForm] = useState({ title: '', description: '', assignedTo: '', deadline: '', priority: 'medium' });
  const [subtaskAssigneeQuery, setSubtaskAssigneeQuery] = useState('');
  const [subtaskAssigneeOpen, setSubtaskAssigneeOpen] = useState(false);
  const [subtaskAssigneeHighlight, setSubtaskAssigneeHighlight] = useState(0);
  const [timelineNote, setTimelineNote] = useState('');

  const filteredSubtaskAssignees = useMemo(() => {
    const simplify = (s) => String(s ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim();
    const qRaw = String(subtaskAssigneeQuery || '');
    const q = simplify(qRaw);
    const qTokens = q.split(/\s+/).filter(Boolean);
    const list = Array.isArray(users) ? users : [];
    if (!q) return list;
    return list.filter(u => {
      const username = simplify(u.username);
      const login = simplify(u.login);
      const firstName = simplify(u.firstName || u.firstname || u.name || u.first || u.givenName || u.given_name);
      const lastName = simplify(u.lastName || u.lastname || u.surname || u.last || u.familyName || u.family_name);
      const patronymic = simplify(u.patronymic || u.middleName || u.middlename || u.middle_name);
      const fio = simplify(u.fio || `${lastName} ${firstName} ${patronymic}`);
      const fullName = simplify(u.fullName || u.fullname || u.nameRu || `${firstName} ${lastName}`);
      const displayName = simplify(u.displayName || u.display || fio || fullName || `${firstName} ${lastName}`);
      const email = simplify(u.email);
      const phone = simplify(u.phone || u.tel || '');
      const id = simplify(u.id);
      const haystack = [username, login, firstName, lastName, patronymic, fio, fullName, displayName, email, phone, id]
        .filter(Boolean)
        .join(' ');
      const allTokensMatch = qTokens.every(tok => haystack.includes(tok));
      const starts = username.startsWith(q) || login.startsWith(q) || firstName.startsWith(q) || lastName.startsWith(q) || displayName.startsWith(q) || fio.startsWith(q) || fullName.startsWith(q);
      const allValues = simplify(Object.values(u).join(' '));
      return allTokensMatch || starts || allValues.includes(q);
    });
  }, [users, subtaskAssigneeQuery]);

  function userParticipatesInTaskTree(uid, rootTaskId, allTasks) {
    const id = String(uid || '');
    const rid = String(rootTaskId);
    const list = Array.isArray(allTasks) ? allTasks : [];
    const rootRow = list.find(t => String(t.id) === rid);
    if (rootRow) {
      const co = parseCoAssigneeIdsFromTask(rootRow.coAssignees);
      if (co.some(cid => String(cid) === id)) return true;
    }
    return list.some(t => {
      const root = String(t.id) === rid;
      const child = String(t.parentTaskId) === rid;
      if (!root && !child) return false;
      return String(t.authorId) === id || String(t.assignedTo) === id;
    });
  }

  function parseTaskActivityLog(raw) {
    if (!raw) return [];
    try {
      const a = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(a) ? a : [];
    } catch {
      return [];
    }
  }

  function openTaskDetail(taskRow) {
    if (!taskRow) return;
    const id = taskRow.parentTaskId ? taskRow.parentTaskId : taskRow.id;
    setViewTaskId(id);
  }

  // Получение текущего пользователя
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(u);
  }, []);

  // Загрузка пользователей
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/users', {
      headers: token ? { 'Authorization': 'Bearer ' + token } : {}
    })
      .then(r => r.json())
      .then(data => {
        console.log('USERS LOADED:', data);
        const arr = Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []);
        setUsers(arr);
      })
      .catch(() => setUsers([]));
  }, []);

  // ВПР: открыть форму «Создать задачу» с прикреплённым файлом расхождений
  useEffect(() => {
    if (!modal || !initialFileForCreate || !(initialFileForCreate instanceof File)) return;
    setShowCreateModal(true);
    setFiles([initialFileForCreate]);
    onConsumedInitialFile?.();
  }, [modal, initialFileForCreate, onConsumedInitialFile]);

  // Загрузка задач
  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    let url = '/api/tasks';
    if (filter === 'open') url = '/api/tasks/open';
    else if (filter === 'completed') url = '/api/tasks/completed';
    
    fetch(url.startsWith('/api/') ? url : url, {
      headers: token ? { 'Authorization': 'Bearer ' + token } : {}
    })
      .then(r => r.json())
      .then(data => { 
        setTasks(data); 
        setLoading(false); 
      })
      .catch(err => { 
        console.error('Error loading tasks:', err);
        setTasks([]); 
        setLoading(false); 
      });
  }, [filter]);

  // Отслеживание ориентации экрана для мобильной версии
  useEffect(() => {
    if (!isMobile) return;
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isMobile]);

  // Корневая задача для карточки просмотра (в списке только родительские)
  const viewRootTask = useMemo(() => {
    if (!viewTaskId) return null;
    const t = tasks.find(x => String(x.id) === String(viewTaskId));
    if (!t) return null;
    if (!t.parentTaskId) return t;
    return tasks.find(x => String(x.id) === String(t.parentTaskId)) || t;
  }, [tasks, viewTaskId]);

  const subtasksOfViewRoot = useMemo(() => {
    if (!viewRootTask) return [];
    return tasks
      .filter(s => String(s.parentTaskId) === String(viewRootTask.id))
      .slice()
      .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  }, [tasks, viewRootTask]);

  const taskDetailTimeline = useMemo(() => {
    if (!viewRootTask) return [];
    const log = parseTaskActivityLog(viewRootTask.activityLog).slice();
    const seen = new Set(
      log.filter(e => e.type === 'completed' && e.taskId != null).map(e => String(e.taskId))
    );
    [viewRootTask, ...subtasksOfViewRoot].forEach(trow => {
      if (
        (trow.status === 'completed' || trow.status === 'closed') &&
        trow.completedAt &&
        !seen.has(String(trow.id))
      ) {
        log.push({
          type: 'completed',
          ts: trow.completedAt,
          taskId: trow.id,
          title: trow.title,
          comment: trow.completionComment,
          isSubtask: !!trow.parentTaskId,
          legacy: true
        });
        seen.add(String(trow.id));
      }
    });
    log.sort((a, b) => new Date(a.ts) - new Date(b.ts));
    return log;
  }, [viewRootTask, subtasksOfViewRoot]);

  // Корректная фильтрация задач по выбранному фильтру (в ленту — только родительские задачи)
  const filtered = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    
    let arr = [];
    if (filter === 'all') arr = tasks;
    else if (filter === 'open') arr = tasks.filter(t => !t.status || t.status === 'open' || (t.status !== 'completed' && t.status !== 'closed'));
    else if (filter === 'completed') arr = tasks.filter(t => t.status === 'completed' || t.status === 'closed');
    else arr = tasks;

    arr = arr.filter(t => !t.parentTaskId);
    
    // Фильтрация по приоритету
    if (priorityFilter !== 'all') {
      arr = arr.filter(t => t.priority === priorityFilter);
    }
    
    // Сортировка: последние задачи сверху
    return arr.slice().sort((a, b) => {
      const dateA = new Date(a.created_at || a.deadline || 0);
      const dateB = new Date(b.created_at || b.deadline || 0);
      return dateB - dateA;
    });
  }, [tasks, filter, priorityFilter]);

  // Быстрый поиск по названию и описанию прямо в ленте
  const visibleTasks = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter(t => `${t.title || ''} ${t.description || ''}`.toLowerCase().includes(q));
  }, [filtered, listQuery]);

  // Статистика по основным задачам (без дублирования подзадач)
  const stats = useMemo(() => {
    if (!Array.isArray(tasks)) return { total: 0, completed: 0, active: 0, high: 0 };
    const roots = tasks.filter(t => !t.parentTaskId);
    return {
      total: roots.length,
      completed: roots.filter(t => t.status === 'completed' || t.status === 'closed').length,
      active: roots.filter(t => t.status !== 'completed' && t.status !== 'closed').length,
      high: roots.filter(t => t.priority === 'high' && t.status !== 'completed' && t.status !== 'closed').length
    };
  }, [tasks]);

  // Таймеры для миниатюр задач
  const [miniTimers, setMiniTimers] = useState({});
  useEffect(() => {
    // Обновлять таймеры каждую секунду
    const updateAllTimers = () => {
      const timers = {};
      filtered.forEach(t => {
        if (!t.deadline) return;
        // Не показывать таймер для "Выполнена" или "Закрыта"
        if (t.status === 'completed' || t.status === 'closed') return;
        const deadline = new Date(t.deadline);
        const now = new Date();
        const diffMs = deadline - now;
        if (diffMs > 0) {
          const hours = Math.floor(diffMs / (1000 * 60 * 60));
          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
          timers[t.id] = `осталось ${hours}ч ${minutes}м ${seconds}с`;
        } else {
          timers[t.id] = 'просрочено';
        }
      });
      setMiniTimers(timers);
    };
    updateAllTimers();
    const interval = setInterval(updateAllTimers, 1000);
    return () => clearInterval(interval);
  }, [filtered]);

  const handleClearCompleted = () => {
    if (!window.confirm('Удалить все выполненные задачи?')) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch('/api/tasks/completed', {
      method: 'DELETE',
      headers: token ? { 'Authorization': 'Bearer ' + token } : {}
    })
      .then(() => {
        setTasks(tsk => tsk.filter(t => t.status !== 'completed' && t.status !== 'closed'));
        setFilter('all');
      });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    
    // Улучшенная валидация с проверкой на пустые строки и пробелы
    const title = (form.title || '').trim();
    const description = (form.description || '').trim();
    // assignedTo может быть числом или строкой, проверяем что это не пустая строка и не null/undefined
    const assignedTo = form.assignedTo;
    const deadline = (form.deadline || '').trim();
    
    console.log('[Tasks] Валидация формы:', { 
      title, 
      description: description.substring(0, 50) + '...', 
      assignedTo, 
      assignedToType: typeof assignedTo,
      deadline,
      titleEmpty: !title,
      descriptionEmpty: !description,
      assignedToEmpty: !assignedTo || assignedTo === '' || assignedTo === '0',
      deadlineEmpty: !deadline,
      fullForm: form
    });
    
    // Проверяем каждое поле отдельно
    const missingFields = [];
    if (!title) missingFields.push('Заголовок');
    if (!description) missingFields.push('Описание');
    if (!assignedTo || assignedTo === '' || assignedTo === '0' || assignedTo === 0) {
      missingFields.push('Исполнитель');
    }
    if (!deadline) missingFields.push('Срок выполнения');
    
    if (missingFields.length > 0) {
      alert(`Пожалуйста, заполните все обязательные поля:\n${missingFields.join('\n')}`);
      return;
    }
    
    const token = localStorage.getItem('token');
    const data = new FormData();
    // Используем очищенные значения
    data.append('title', title);
    data.append('description', description);
    data.append('assignedTo', assignedTo);
    data.append('deadline', deadline);
    data.append('priority', form.priority || 'medium');
    const coList = (form.coAssigneeIds || [])
      .map(id => parseInt(String(id), 10))
      .filter(n => !Number.isNaN(n) && n > 0 && String(n) !== String(assignedTo))
      .filter((n, i, a) => a.indexOf(n) === i)
      .slice(0, MAX_TASK_CO_ASSIGNEES);
    data.append('coAssignees', JSON.stringify(coList));

    if (files && files.length > 0) {
      const names = [];
      for (let i = 0; i < files.length; i++) {
        data.append('files', files[i]);
        names.push(files[i].name);
      }
      data.append('originalName', JSON.stringify(names));
    }
    
    console.log('[Tasks] Отправка задачи:', { title, assignedTo, deadline, priority: form.priority });
    
    fetch('/api/tasks', {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: data
    })
      .then(async r => {
        if (!r.ok) {
          const errorData = await r.json().catch(() => ({ error: 'Ошибка сервера' }));
          throw new Error(errorData.error || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(newTask => {
        console.log('[Tasks] Задача успешно создана:', newTask);
        // Закрываем модалку
        setShowCreateModal(false);
        // Сбрасываем форму
        setForm({ title: '', description: '', assignedTo: '', coAssigneeIds: [], deadline: '', priority: 'medium' });
        setCoAssigneeQuery('');
        setFiles([]);
        // Обновляем список задач
        const token = localStorage.getItem('token');
        fetch('/api/tasks', {
          headers: token ? { 'Authorization': 'Bearer ' + token } : {}
        })
          .then(async r => {
            if (!r.ok) {
              throw new Error(`HTTP ${r.status}`);
            }
            return r.json();
          })
          .then(data => {
            console.log('[Tasks] Список задач обновлен:', data?.length || 0, 'задач');
            setTasks(Array.isArray(data) ? data : []);
          })
          .catch(err => {
            console.error('[Tasks] Ошибка загрузки задач:', err);
            setTasks([]);
          });
      })
      .catch(err => {
        console.error('[Tasks] Ошибка создания задачи:', err);
        alert('Ошибка создания задачи: ' + err.message);
      });
  };

  // Для отображения выбранных файлов и удаления
  const handleFileChange = e => {
    setFiles(Array.from(e.target.files));
  };

  const handleRemoveFile = idx => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDelete = id => {
    const token = localStorage.getItem('token');
    fetch(`/api/tasks/${id}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': 'Bearer ' + token } : {}
    })
      .then(() => setTasks(tsk => tsk.filter(t => t.id !== id)));
  };


  const reloadTasks = () => {
    const token = localStorage.getItem('token');
    let url = '/api/tasks';
    if (filter === 'open') url = '/api/tasks/open';
    else if (filter === 'completed') url = '/api/tasks/completed';
    fetch(url.startsWith('/api/') ? url : url, {
      headers: token ? { 'Authorization': 'Bearer ' + token } : {}
    })
      .then(r => r.json())
      .then(data => setTasks(Array.isArray(data) ? data : []))
      .catch(() => setTasks([]));
  };

  const handleComplete = id => {
    setCommentError('');
    setIsCompletionRequired(false);
    const token = localStorage.getItem('token');
    fetch(`/api/tasks/${id}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ comment: (comment || '').trim() })
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || r.status);
        }
        return r.json();
      })
      .then(() => {
        setShowComplete(null);
        setComment('');
        setCommentError('');
        reloadTasks();
      })
      .catch(err => {
        alert(err.message || 'Ошибка завершения');
      });
  };

  const handleAddSubtask = e => {
    e.preventDefault();
    if (!viewRootTask) return;
    const title = (subtaskForm.title || '').trim();
    const assignedTo = subtaskForm.assignedTo;
    const deadline = (subtaskForm.deadline || '').trim();
    if (!title || !assignedTo || !deadline) {
      alert('Укажите заголовок, исполнителя и срок подзадачи');
      return;
    }
    const token = localStorage.getItem('token');
    fetch('/api/tasks/subtask', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        parentTaskId: viewRootTask.id,
        title,
        description: (subtaskForm.description || '').trim(),
        assignedTo,
        deadline,
        priority: subtaskForm.priority || 'medium'
      })
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || r.status);
        }
        return r.json();
      })
      .then(() => {
        setSubtaskForm({ title: '', description: '', assignedTo: '', deadline: '', priority: 'medium' });
        setSubtaskAssigneeQuery('');
        reloadTasks();
      })
      .catch(err => alert(err.message || 'Ошибка создания подзадачи'));
  };

  const handleSaveTimelineNote = () => {
    if (!viewRootTask || !timelineNote.trim()) return;
    const token = localStorage.getItem('token');
    fetch(`/api/tasks/${viewRootTask.id}/note`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ text: timelineNote.trim() })
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || r.status);
        }
        return r.json();
      })
      .then(() => {
        setTimelineNote('');
        reloadTasks();
      })
      .catch(err => alert(err.message || 'Ошибка'));
  };

  const handleExtend = id => {
    const token = localStorage.getItem('token');
    fetch(`/api/tasks/${id}/extend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ deadline: newDeadline })
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || r.status);
        }
        return r.json();
      })
      .then(updated => {
        setTasks(tsk => tsk.map(t => t.id === id ? updated : t));
        setShowExtend(null);
        setNewDeadline('');
        reloadTasks();
      })
      .catch(err => alert(err.message || 'Ошибка продления'));
  };

  const handleFileDownload = async (filename, originalName) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Downloading file:', filename, 'with token:', token ? 'present' : 'missing');
      
      const response = await fetch(`/api/download/${filename}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      console.log('Download response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download error response:', errorText);
        throw new Error(`Ошибка при скачивании файла: ${response.status} ${errorText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
      alert(`Ошибка при скачивании файла: ${error.message}`);
    }
  };

  const getDeadlineCountdown = (deadline) => {
    if (!deadline) return '';
    const now = new Date();
    const end = new Date(deadline);
    const diffMs = end - now;
    if (diffMs <= 0) return 'Дедлайн истёк';
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    let result = '';
    if (days > 0) result += `${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'} `;
    if (hours > 0) result += `${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'} `;
    if (minutes > 0) result += `${minutes} ${minutes === 1 ? 'минута' : minutes < 5 ? 'минуты' : 'минут'} `;
    return result.trim();
  };

  const modalDeadlineStr = viewRootTask && viewRootTask.deadline
    ? new Date(viewRootTask.deadline).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

  const deadlineCountdown = viewRootTask && viewRootTask.deadline ? getDeadlineCountdown(viewRootTask.deadline) : '';

  useEffect(() => {
    if (!viewRootTask || !viewRootTask.deadline) {
      setDeadlineTimer('');
      return;
    }
    
    // Если задача выполнена, не показываем таймер
    if (viewRootTask.status === 'completed' || viewRootTask.status === 'closed') {
      setDeadlineTimer('');
      return;
    }
    
    const updateTimer = () => {
      const deadline = new Date(viewRootTask.deadline);
      const now = new Date();
      const diffMs = deadline - now;
      if (diffMs > 0) {
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        setDeadlineTimer(`осталось ${hours}ч ${minutes}м ${seconds}с`);
      } else {
        setDeadlineTimer('просрочено');
      }
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [viewRootTask?.id, viewRootTask?.deadline, viewRootTask?.status]);

  return (
    <div className={`nd-tdWrap${isMobile ? ' nd-mobile' : ''}`} data-modal="tasks-board">
      <div className="nd-tdSheet">
        <div className="nd-sheetHead">
          <div className="nd-sheetIco">✅</div>
          <h2>
            Задачи
            <span>планирование · приоритеты · контроль выполнения</span>
          </h2>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          {onClose && (
            <button type="button" className="nd-sheetClose" onClick={onClose} aria-label="Закрыть" title="Закрыть">
              <FiX />
            </button>
          )}
        </div>

        <div className="nd-tdStats">
          <div className="nd-tdStat nd-glass" style={{ '--sc': 'var(--nd-blue)' }}>
            <b>{stats.total}</b>
            <span>Всего</span>
          </div>
          <div className="nd-tdStat nd-glass" style={{ '--sc': 'var(--nd-green)' }}>
            <b>{stats.completed}</b>
            <span>Выполнено</span>
          </div>
          <div className="nd-tdStat nd-glass" style={{ '--sc': 'var(--nd-amber)' }}>
            <b>{stats.active}</b>
            <span>Активных</span>
          </div>
          <div className="nd-tdStat nd-glass" style={{ '--sc': 'var(--nd-red)' }}>
            <b>{stats.high}</b>
            <span>Важных</span>
          </div>
        </div>

        <div className="nd-progWrap">
          <i style={{ width: `${stats.total ? Math.round((stats.completed / stats.total) * 100) : 0}%` }} />
        </div>

        <div className="nd-tdForm nd-glass">
          <div className="nd-fRow">
            <div className="nd-tdSearch" style={{ maxWidth: 'none' }}>
              <span className="nd-sIco"><FiSearch /></span>
              <input
                type="text"
                placeholder="Поиск задач…"
                value={listQuery}
                onChange={e => setListQuery(e.target.value)}
              />
            </div>
            <button type="button" className="nd-chip" onClick={() => setShowSearchModal(true)} title="Поиск по сотруднику и дате">
              🔍 Расширенный поиск
            </button>
            <button type="button" className="nd-addBtn" onClick={() => setShowCreateModal(true)}>＋ Создать задачу</button>
          </div>
        </div>

        <div className="nd-toolRow">
          <button type="button" className={`nd-chip${filter === 'all' ? ' nd-on' : ''}`} onClick={() => setFilter('all')}>Все</button>
          <button type="button" className={`nd-chip${filter === 'open' ? ' nd-on' : ''}`} onClick={() => setFilter('open')}>Открытые</button>
          <button type="button" className={`nd-chip${filter === 'completed' ? ' nd-on' : ''}`} onClick={() => setFilter('completed')}>Выполненные</button>

          <span className="nd-sepV" />

          <button type="button" className={`nd-chip${priorityFilter === 'all' ? ' nd-on' : ''}`} style={{ '--cc': 'var(--nd-violet)' }} onClick={() => setPriorityFilter('all')}>
            <FaFilter size={11} /> Все приоритеты
          </button>
          <button type="button" className={`nd-chip${priorityFilter === 'high' ? ' nd-on' : ''}`} style={{ '--cc': 'var(--nd-red)' }} onClick={() => setPriorityFilter('high')}>Высокий</button>
          <button type="button" className={`nd-chip${priorityFilter === 'medium' ? ' nd-on' : ''}`} style={{ '--cc': 'var(--nd-amber)' }} onClick={() => setPriorityFilter('medium')}>Средний</button>
          <button type="button" className={`nd-chip${priorityFilter === 'low' ? ' nd-on' : ''}`} style={{ '--cc': 'var(--nd-green)' }} onClick={() => setPriorityFilter('low')}>Низкий</button>

          {user?.role === 'admin' && (
            <>
              <span className="nd-sepV" />
              <button type="button" className="nd-chip nd-danger" onClick={handleClearCompleted}>
                <FiTrash2 size={12} /> Очистить выполненные
              </button>
            </>
          )}
        </div>

        {loading && <div style={{ color: 'var(--nd-acc)', fontWeight: 700, fontSize: 12.5 }}>Загрузка…</div>}

        <div className="nd-taskList">
          {visibleTasks.length === 0 && !loading && (
            <div className="nd-tdEmpty">
              <span className="nd-eIco">🍃</span>
              Нет задач по выбранному фильтру
            </div>
          )}
          {visibleTasks.map((t, idx) => {
            const authorUser = users.find(u => String(u.id) === String(t.authorId));
            const stripHtmlTags = (html) => {
              if (!html) return '';
              const tmp = document.createElement('div');
              tmp.innerHTML = html;
              return tmp.textContent || tmp.innerText || '';
            };

            const cleanDescription = t.description ? stripHtmlTags(t.description) : '';
            const shortDesc = cleanDescription
              ? cleanDescription.split(/\s+/).slice(0, 8).join(' ') + (cleanDescription.split(/\s+/).length > 8 ? '…' : '')
              : '';

            const miniTimer = miniTimers[t.id] || '';
            const isDone = t.status === 'completed' || t.status === 'closed';
            const priorityColor = t.priority === 'high'
              ? 'var(--nd-red)'
              : t.priority === 'low' ? 'var(--nd-green)' : 'var(--nd-amber)';
            const priorityCls = t.priority === 'high' ? 'nd-high' : t.priority === 'low' ? 'nd-low' : 'nd-mid';
            const priorityLabel = t.priority === 'high' ? 'Высокий' : t.priority === 'low' ? 'Низкий' : 'Средний';
            const subs = tasks.filter(s => String(s.parentTaskId) === String(t.id));
            const openSubs = subs.filter(s => s.status !== 'completed' && s.status !== 'closed').length;
            const authorName = authorUser
              ? (`${authorUser.firstName || ''} ${authorUser.lastName || ''}`.trim() || authorUser.username || 'Неизвестный')
              : 'Неизвестный';

            const handleTaskSwipeStart = (e) => {
              if (!isMobile) return;
              e.stopPropagation();
              setSwipeData(prev => ({ ...prev, [t.id]: { startX: e.touches[0].clientX } }));
            };

            const handleTaskSwipeMove = (e) => {
              if (!isMobile || !swipeData[t.id]) return;
              e.stopPropagation();
            };

            const handleTaskSwipeEnd = (e) => {
              if (!isMobile || !swipeData[t.id]) return;
              e.stopPropagation();
              const distance = e.changedTouches[0].clientX - swipeData[t.id].startX;
              if (distance > 50) setViewTaskId(t.id);
              setSwipeData(prev => {
                const next = { ...prev };
                delete next[t.id];
                return next;
              });
            };

            return (
              <div
                key={t.id}
                className={`nd-task nd-glass${isDone ? ' nd-done' : ''}`}
                style={{ '--pc': priorityColor, animationDelay: `${Math.min(idx * 40, 400)}ms`, cursor: isMobile ? 'pointer' : undefined }}
                onTouchStart={isMobile ? handleTaskSwipeStart : undefined}
                onTouchMove={isMobile ? handleTaskSwipeMove : undefined}
                onTouchEnd={isMobile ? handleTaskSwipeEnd : undefined}
              >
                <div className="nd-taskAv">
                  {authorUser && authorUser.avatar
                    ? <img src={authorUser.avatar} alt="avatar" />
                    : <FaUserCircle size={28} />}
                </div>

                <div className="nd-tMain">
                  <b>
                    {t.title}
                    {subs.length > 0 && (
                      <span className="nd-subCount">
                        · {subs.length} подзадач{openSubs ? ` (${openSubs} открыто)` : ''}
                      </span>
                    )}
                  </b>
                  <span className="nd-tMeta">
                    <span>от {authorName}</span>
                    <span>🕒 {t.deadline ? new Date(t.deadline).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                    {t.deadline && !isDone && miniTimer && (
                      <span className={`nd-tTimer${miniTimer === 'просрочено' ? ' nd-late' : ''}`}>
                        {miniTimer === 'просрочено' ? '⏰ Просрочено' : miniTimer}
                      </span>
                    )}
                    {shortDesc && !isMobile && <span className="nd-tDesc">{shortDesc}</span>}
                  </span>
                </div>

                <span className={`nd-prB ${priorityCls}`}>{priorityLabel}</span>

                <span className={`nd-stB ${isDone ? 'nd-ok' : 'nd-pend'}`}>
                  {isDone
                    ? <><FaCheckCircle size={11} /> {t.completedAt ? new Date(t.completedAt).toLocaleDateString('ru-RU') : 'Выполнена'}</>
                    : <><FaHourglassHalf size={11} /> Открыта</>}
                </span>

                <div className="nd-taskActs">
                  <button type="button" className="nd-aBtn nd-okB" onClick={() => setViewTaskId(t.id)}>
                    {isMobile ? <FiChevronRight size={16} /> : 'Открыть'}
                  </button>
                  {isDone && !isMobile && (
                    <button type="button" className="nd-aBtn" onClick={() => setShowComment(t.id)}>Комментарий</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCreateModal && (
        <ModalBg lightBackdrop onClose={()=>{setShowCreateModal(false);}}>
          <div style={{
            position:'relative',
            overflow:'hidden',
            borderRadius:18,
            border:'1px solid rgba(167, 139, 250, 0.28)',
            boxShadow:'0 0 0 1px rgba(255,255,255,0.6) inset, 0 4px 24px rgba(99, 102, 241, 0.12), 0 25px 50px -12px rgba(15, 23, 42, 0.2)',
            background:'radial-gradient(ellipse 90% 60% at 100% -10%, rgba(56, 189, 248, 0.16), transparent), radial-gradient(ellipse 70% 50% at -5% 100%, rgba(167, 139, 250, 0.14), transparent), linear-gradient(165deg, #ffffff 0%, #f8fafc 46%, #eef2ff 100%)',
            padding:'28px 32px 32px',
            color:'#0f172a',
            width:'min(1400px, 98vw)',
            height:'98vh',
            margin:'20px auto',
            display:'flex',
            flexDirection:'column'
          }}>
            <div aria-hidden style={{position:'absolute',top:0,left:0,right:0,height:5,background:'linear-gradient(90deg, #22d3ee 0%, #818cf8 45%, #34d399 100%)',opacity:0.92}} />
            <div aria-hidden style={{position:'absolute',top:64,right:-48,width:200,height:200,borderRadius:'50%',background:'radial-gradient(circle, rgba(129,140,246,0.2) 0%, transparent 68%)',pointerEvents:'none'}} />
            <div aria-hidden style={{position:'absolute',bottom:20,left:-36,width:160,height:160,borderRadius:'50%',background:'radial-gradient(circle, rgba(45,212,191,0.16) 0%, transparent 70%)',pointerEvents:'none'}} />
            <header style={{position:'relative',zIndex:1,marginBottom:22,paddingTop:6,display:'flex',alignItems:'flex-start',gap:16}}>
              <span style={{
                flexShrink:0,
                width:52,
                height:52,
                borderRadius:14,
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
                fontSize:'1.55rem',
                background:'linear-gradient(145deg, #e0e7ff 0%, #ccfbf1 100%)',
                boxShadow:'0 4px 14px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255,255,255,0.85)',
                border:'1px solid rgba(129, 140, 246, 0.35)'
              }} aria-hidden>📝</span>
              <div style={{flex:1,minWidth:0}}>
                <h3 style={{
                  margin:0,
                  fontWeight:800,
                  fontSize:'1.75rem',
                  lineHeight:1.2,
                  letterSpacing:'-0.02em',
                  background:'linear-gradient(105deg, #0f766e 0%, #4f46e5 55%, #0891b2 100%)',
                  WebkitBackgroundClip:'text',
                  WebkitTextFillColor:'transparent',
                  backgroundClip:'text'
                }}>Создать задачу</h3>
                <p style={{margin:'8px 0 0',fontSize:'0.92rem',color:'#64748b',fontWeight:500,lineHeight:1.45,maxWidth:'42em'}}>
                  Новая задача для команды — заголовок, срок и исполнитель помогут ничего не потерять.
                </p>
              </div>
            </header>
            <div style={{flex:1,overflowY:'auto',maxHeight:'88vh',paddingRight:'8px',position:'relative',zIndex:1}}>
              <Form onSubmit={handleSubmit} style={{padding:'0',marginBottom:8,gap:12,display:'flex',flexDirection:'column',alignItems:'stretch',width:'100%',background:'transparent',border:'none',boxShadow:'none'}}>
              <div style={{display:'flex',gap:8,width:'100%',flexWrap:'wrap',justifyContent:'flex-start'}}>
                <Input name="title" placeholder="Заголовок" value={form.title} onChange={handleChange} required style={{flex:'1 1 320px',background:'rgba(255,255,255,0.92)',border:'1px solid rgba(129, 140, 246, 0.22)',color:'#0f172a',height:48,boxShadow:'inset 0 1px 2px rgba(99,102,241,0.06), 0 1px 3px rgba(15,23,42,0.06)',borderRadius:12}} />
                <textarea
                  name="description"
                  placeholder="Описание"
                  value={form.description}
                  onChange={handleChange}
                  required
                  style={{
                    width:'100%',
                    minHeight:240,
                    borderRadius:14,
                    border:'1px solid rgba(129, 140, 246, 0.22)',
                    background:'rgba(255,255,255,0.92)',
                    color:'#0f172a',
                    padding:14,
                    boxSizing:'border-box',
                    fontSize:'1.02em',
                    height: '260px',
                    resize: 'vertical',
                    boxShadow:'inset 0 1px 2px rgba(99,102,241,0.06), 0 1px 3px rgba(15,23,42,0.06)'
                  }}
                />
              </div>

              <div style={{display:'flex',gap:10,width:'100%',flexWrap:'wrap',justifyContent:'flex-start',alignItems:'stretch'}}>
                <div style={{position:'relative', width:'100%', maxWidth:360}}>
                  <div style={{color:'#475569',fontSize:'0.85em',marginBottom:'4px',fontWeight:500}}>Добавить исполнителя</div>
                  <Input
                    placeholder="Выберите исполнителя"
                    value={assigneeQuery}
                    onChange={e=>{ setAssigneeQuery(e.target.value); setAssigneeOpen(true); setAssigneeHighlight(0); }}
                    onFocus={()=> setAssigneeOpen(true)}
                    onBlur={()=> setTimeout(()=> setAssigneeOpen(false), 150)}
                    onKeyDown={(e)=>{
                      const len = (filteredAssignees||[]).length;
                      if(!len) return;
                      if(e.key==='ArrowDown'){ e.preventDefault(); setAssigneeHighlight(h=> Math.min(h+1, len-1)); }
                      if(e.key==='ArrowUp'){ e.preventDefault(); setAssigneeHighlight(h=> Math.max(h-1, 0)); }
                      if(e.key==='Enter'){
                        e.preventDefault();
                        const u = filteredAssignees[assigneeHighlight];
                        if(u){ setForm(prev=>({...prev, assignedTo: u.id})); setAssigneeQuery(getAssigneeLabel(u)); setAssigneeOpen(false); }
                      }
                      if(e.key==='Escape'){ setAssigneeOpen(false); }
                    }}
                    style={{height:48, border:'1px solid rgba(129, 140, 246, 0.22)', background:'rgba(255,255,255,0.95)', color:'#0f172a', borderRadius:12, boxShadow:'inset 0 1px 2px rgba(99,102,241,0.05)'}}
                  />
                  {assigneeOpen && (
                    <ul style={{position:'absolute', top:72, left:0, right:0, background:'rgba(255,255,255,0.98)', border:'1px solid rgba(129, 140, 246, 0.2)', borderRadius:12, boxShadow:'0 16px 40px rgba(99, 102, 241, 0.14), 0 4px 12px rgba(15, 23, 42, 0.08)', margin:0, padding:'6px', listStyle:'none', maxHeight:260, overflowY:'auto', zIndex:5}}>
                      {filteredAssignees && filteredAssignees.length === 0 && (
                        <li style={{padding:'10px 12px', color:'#64748b'}}>Ничего не найдено</li>
                      )}
                      {(filteredAssignees||[]).map((u, idx)=>{
                        const active = idx === assigneeHighlight;
                        return (
                          <li key={u.id}
                              onMouseEnter={()=>setAssigneeHighlight(idx)}
                              onMouseDown={(e)=>{ e.preventDefault(); setForm(prev=>({...prev, assignedTo: u.id})); setAssigneeQuery(getAssigneeLabel(u)); setAssigneeOpen(false); }}
                              style={{padding:'10px 12px', borderRadius:10, cursor:'pointer', background: active? 'rgba(124,58,237,0.12)' : 'transparent', color:'#0f172a'}}>
                            {getAssigneeLabel(u)}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div style={{ width: '100%', maxWidth: 520 }}>
                  <div style={{ color: '#475569', fontSize: '0.85em', marginBottom: 4, fontWeight: 500 }}>
                    Со-исполнители (до {MAX_TASK_CO_ASSIGNEES}, необязательно)
                  </div>
                  {(form.coAssigneeIds || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                      {(form.coAssigneeIds || []).map((cid) => {
                        const u = (Array.isArray(users) ? users : []).find(x => String(x.id) === String(cid));
                        const label = getAssigneeLabel(u) || String(cid);
                        return (
                          <span
                            key={String(cid)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '6px 10px',
                              borderRadius: 999,
                              background: 'rgba(129, 140, 246, 0.12)',
                              border: '1px solid rgba(129, 140, 246, 0.35)',
                              fontSize: '0.82rem',
                              color: '#0f172a',
                              fontWeight: 600
                            }}
                          >
                            {label}
                            <button
                              type="button"
                              aria-label={`Убрать ${label}`}
                              onClick={() => setForm(prev => ({
                                ...prev,
                                coAssigneeIds: (prev.coAssigneeIds || []).filter(id => String(id) !== String(cid))
                              }))}
                              style={{
                                border: 'none',
                                background: 'rgba(239, 68, 68, 0.15)',
                                color: '#b91c1c',
                                borderRadius: 999,
                                width: 22,
                                height: 22,
                                cursor: 'pointer',
                                fontWeight: 800,
                                lineHeight: 1,
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ position: 'relative', width: '100%', maxWidth: 360 }}>
                    <Input
                      placeholder={(form.coAssigneeIds || []).length >= MAX_TASK_CO_ASSIGNEES ? 'Лимит со-исполнителей' : 'Добавить со-исполнителя'}
                      value={coAssigneeQuery}
                      disabled={(form.coAssigneeIds || []).length >= MAX_TASK_CO_ASSIGNEES}
                      onChange={e => { setCoAssigneeQuery(e.target.value); setCoAssigneeOpen(true); setCoAssigneeHighlight(0); }}
                      onFocus={() => setCoAssigneeOpen(true)}
                      onBlur={() => setTimeout(() => setCoAssigneeOpen(false), 150)}
                      onKeyDown={(e) => {
                        const len = (filteredCoAssigneePickList || []).length;
                        if (!len) return;
                        if (e.key === 'ArrowDown') { e.preventDefault(); setCoAssigneeHighlight(h => Math.min(h + 1, len - 1)); }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setCoAssigneeHighlight(h => Math.max(h - 1, 0)); }
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const u = filteredCoAssigneePickList[coAssigneeHighlight];
                          if (!u || (form.coAssigneeIds || []).length >= MAX_TASK_CO_ASSIGNEES) return;
                          if (String(u.id) === String(form.assignedTo)) return;
                          if ((form.coAssigneeIds || []).some(id => String(id) === String(u.id))) return;
                          setForm(prev => ({ ...prev, coAssigneeIds: [...(prev.coAssigneeIds || []), u.id] }));
                          setCoAssigneeQuery('');
                          setCoAssigneeOpen(false);
                        }
                        if (e.key === 'Escape') setCoAssigneeOpen(false);
                      }}
                      style={{ height: 48, border: '1px solid rgba(129, 140, 246, 0.22)', background: 'rgba(255,255,255,0.95)', color: '#0f172a', borderRadius: 12, boxShadow: 'inset 0 1px 2px rgba(99,102,241,0.05)' }}
                    />
                    {coAssigneeOpen && (form.coAssigneeIds || []).length < MAX_TASK_CO_ASSIGNEES && (
                      <ul style={{ position: 'absolute', top: 52, left: 0, right: 0, background: 'rgba(255,255,255,0.98)', border: '1px solid rgba(129, 140, 246, 0.2)', borderRadius: 12, boxShadow: '0 16px 40px rgba(99, 102, 241, 0.14), 0 4px 12px rgba(15, 23, 42, 0.08)', margin: 0, padding: '6px', listStyle: 'none', maxHeight: 220, overflowY: 'auto', zIndex: 6 }}>
                        {filteredCoAssigneePickList && filteredCoAssigneePickList.length === 0 && (
                          <li style={{ padding: '10px 12px', color: '#64748b' }}>Нет доступных пользователей</li>
                        )}
                        {(filteredCoAssigneePickList || []).map((u, idx) => {
                          const active = idx === coAssigneeHighlight;
                          return (
                            <li
                              key={u.id}
                              onMouseEnter={() => setCoAssigneeHighlight(idx)}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if ((form.coAssigneeIds || []).length >= MAX_TASK_CO_ASSIGNEES) return;
                                if (String(u.id) === String(form.assignedTo)) return;
                                if ((form.coAssigneeIds || []).some(id => String(id) === String(u.id))) return;
                                setForm(prev => ({ ...prev, coAssigneeIds: [...(prev.coAssigneeIds || []), u.id] }));
                                setCoAssigneeQuery('');
                                setCoAssigneeOpen(false);
                              }}
                              style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', background: active ? 'rgba(124,58,237,0.12)' : 'transparent', color: '#0f172a' }}
                            >
                              {getAssigneeLabel(u)}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                <Input name="deadline" type="datetime-local" value={form.deadline} onChange={handleChange} required style={{minWidth:200,height:48,background:'rgba(255,255,255,0.92)',border:'1px solid rgba(45, 212, 191, 0.25)',color:'#0f172a',borderRadius:12,boxShadow:'inset 0 1px 2px rgba(45,212,191,0.06)'}} />
                <PrioritySelect name="priority" value={form.priority} onChange={handleChange} style={{minWidth:140,height:48,background:'rgba(255,255,255,0.92)',border:'1px solid rgba(245, 158, 11, 0.28)',color:'#0f172a',borderRadius:12,boxShadow:'inset 0 1px 2px rgba(245,158,11,0.06)'}}>
                  <option value="low">Низкий</option>
                  <option value="medium">Средний</option>
                  <option value="high">Высокий</option>
                </PrioritySelect>
                <label htmlFor="file-upload" style={{
                  background:'linear-gradient(135deg, #6366f1 0%, #0ea5e9 50%, #14b8a6 100%)',
                  color:'#fff',
                  border:'none',
                  borderRadius:10,
                  padding:'8px 12px',
                  cursor:'pointer',
                  display:'inline-flex',
                  alignItems:'center',
                  gap:6,
                  transition:'transform .18s, box-shadow .18s',
                  width:'auto',
                  maxWidth:200,
                  boxSizing:'border-box',
                  fontSize:'0.8em',
                  fontWeight:600,
                  boxShadow:'0 4px 14px rgba(99, 102, 241, 0.35)'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24"><path fill="#fff" d="M16.5 12a.75.75 0 0 1 .75.75v5.5A2.75 2.75 0 0 1 14.5 21h-5A2.75 2.75 0 0 1 6.75 18.25v-5.5a.75.75 0 0 1 1.5 0v5.5c0 .69.56 1.25 1.25 1.25h5c.69 0 1.25-.56 1.25-1.25v-5.5a.75.75 0 0 1 .75-.75Zm-4.22-7.53a.75.75 0 0 1 1.44 0l2.25 6.5a.75.75 0 0 1-1.44.46l-.53-1.53H9.5l-.53 1.53a.75.75 0 0 1-1.44-.46l2.25-6.5ZM12 5.62 10.9 8.5h2.2L12 5.62Z"/></svg>
                  <span>Добавить файл</span>
                  <input id="file-upload" type="file" multiple onChange={handleFileChange} style={{display:'none'}} />
                </label>
              </div>

              {/* Список выбранных файлов */}
              {files && files.length > 0 && (
                <div style={{marginTop:-10,marginBottom:10,width:'100%'}}>
                  <ul style={{listStyle:'none',padding:0,margin:0,display:'flex',flexWrap:'wrap',gap:8}}>
                    {files.map((file, idx) => {
                      const ext = file.name.split('.').pop().toLowerCase();
                      const icon = fileIconMap[ext] || fileIconMap.default;
                      return (
                        <li key={file.name+idx} style={{background:'#eafaf1',border:'1px solid #43e97b55',borderRadius:8,padding:'4px 10px',display:'flex',alignItems:'center',gap:8}}>
                          <img src={icon} alt={ext} style={{width:24,height:24,objectFit:'contain'}} />
                          <span style={{color:'#2193b0',fontWeight:600}}>{file.name}</span>
                          <button type="button" onClick={()=>handleRemoveFile(idx)} style={{background:'none',border:'none',color:'#e74c3c',fontWeight:700,fontSize:'1.1em',cursor:'pointer',marginLeft:4}} title="Удалить файл">×</button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div style={{display:'flex',gap:6,width:'100%',flexWrap:'nowrap',justifyContent:'flex-end'}}>
                <Button type="button" onClick={()=>setShowCreateModal(false)} style={{height:34,padding:'0 14px',fontSize:'0.8em',fontWeight:700,borderRadius:10,background:'linear-gradient(180deg, #ffffff, #f1f5ff)',border:'1px solid rgba(129, 140, 246, 0.35)',boxShadow:'0 2px 8px rgba(99,102,241,0.1)',color:'#475569'}}>Отмена</Button>
                <Button type="submit" style={{height:34,padding:'0 14px',fontSize:'0.8em',fontWeight:700,borderRadius:10,background:'linear-gradient(95deg, #6366f1 0%, #3b82f6 45%, #06b6d4 100%)',boxShadow:'0 6px 20px rgba(99,102,241,0.35)',color:'#fff',border:'none'}}>Отправить</Button>
              </div>
              </Form>
            </div>
          </div>
        </ModalBg>
      )}

      {showComplete && (
        <ModalBg onClose={()=>{setShowComplete(null);setComment('');setCommentError('');}}>
          <div style={{
            maxWidth:'520px',minWidth:'320px',
            background:'rgba(17,24,39,0.75)', border:'1px solid rgba(255,255,255,0.08)',
            borderRadius:'16px', boxShadow:'0 20px 60px rgba(2,6,23,0.45)',
            padding:'28px 22px 22px 22px', margin:'60px auto',
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            position:'relative', zIndex:10000
          }}>
            <h3 style={{color:'#e2e8f0',marginBottom:16,fontSize:'1.12em',textAlign:'center'}}>Комментарий к выполнению</h3>
            <textarea
              value={comment}
              onChange={e=>{setComment(e.target.value);setCommentError('');}}
              placeholder="Необязательно, например: Выполнено"
              style={{
                width:'100%',
                minHeight:96,
                borderRadius:12,
                border: commentError ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.14)',
                background:'rgba(17,24,39,0.65)',
                color:'#e5e7eb',
                padding:12
              }}
            />
            {commentError && (
              <div style={{
                color:'#ef4444',
                fontSize:'0.9rem',
                marginTop:8,
                textAlign:'center',
                fontWeight:600
              }}>
                {commentError}
              </div>
            )}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:6}}>
              <button type="button" onClick={()=>{setShowComplete(null);setComment('');setCommentError('');}} style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',color:'#e2e8f0',borderRadius:10,padding:'8px 14px',fontWeight:700,cursor:'pointer'}}>Отмена</button>
              <Button style={{padding:'8px 16px',fontWeight:800}} onClick={()=>handleComplete(showComplete)}>Отправить</Button>
            </div>
          </div>
        </ModalBg>
      )}

      {showExtend && (
        <ModalBg onClose={()=>{setShowExtend(null);setNewDeadline('');}}>
          <div style={{
            maxWidth:'480px', minWidth:'300px',
            background:'rgba(17,24,39,0.75)', border:'1px solid rgba(255,255,255,0.08)',
            borderRadius:'16px', boxShadow:'0 20px 60px rgba(2,6,23,0.45)',
            padding:'24px 22px 22px 22px', margin:'40px auto', display:'flex', flexDirection:'column', gap:12
          }}>
            <h3 style={{color:'#e2e8f0',margin:0,fontSize:'1.12em',textAlign:'center'}}>Продлить дедлайн</h3>
            <input
              type="datetime-local"
              value={newDeadline}
              onChange={e=>setNewDeadline(e.target.value)}
              style={{width:'100%',borderRadius:12,border:'1px solid rgba(255,255,255,0.14)',background:'rgba(17,24,39,0.65)',color:'#e5e7eb',padding:10}}
            />
            <Button onClick={()=>handleExtend(showExtend)} style={{width:'100%'}}>Продлить</Button>
          </div>
        </ModalBg>
      )}

      {viewRootTask && (
        <ModalBg onClose={()=>setViewTaskId(null)}>
          <div
            style={
              isMobile
                ? {
                    background: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
                    padding:
                      '12px max(10px, env(safe-area-inset-left)) 20px max(10px, env(safe-area-inset-right))',
                    color: '#222',
                    width: '100%',
                    maxWidth: 'min(880px, 100%)',
                    minWidth: 0,
                    maxHeight: 'min(92vh, calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px))',
                    overflowY: 'auto',
                    margin: '6px auto 10px',
                    position: 'relative',
                    transform: 'none',
                    boxSizing: 'border-box',
                  }
                : {
                    background: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 16,
                    boxShadow: '0 20px 60px rgba(196, 186, 186, 0.12)',
                    padding: '32px',
                    color: '#222',
                    width: 'min(880px, 92vw)',
                    maxHeight: '92vh',
                    overflowY: 'auto',
                    margin: '20px auto',
                    position: 'relative',
                    transform: 'translateX(20px)',
                    boxSizing: 'border-box',
                  }
            }
          >
            <div style={{fontWeight:700,fontSize:'1.4rem',color:'#6dd5ed',marginBottom:16}}>
              {viewRootTask.title || 'Без заголовка'}
            </div>
            <div style={{fontSize:'0.85rem',color:'#64748b',marginBottom:16}}>
              Основная задача: подзадачи можно закрывать в любом порядке, следующую можно начинать, не дожидаясь предыдущей.
            </div>
            <div style={{fontSize:'1rem',color:'#222',marginBottom:24,whiteSpace:'pre-line',lineHeight:1.5}}>
              {(() => {
                if (!viewRootTask.description) return 'Без описания';
                const tmp = document.createElement('div');
                tmp.innerHTML = viewRootTask.description;
                return tmp.textContent || tmp.innerText || 'Без описания';
              })()}
            </div>

            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,padding:'12px 16px',background:'#f0f4f8',borderRadius:12}}>
              {(() => {
                try {
                  const assignee = (Array.isArray(users) ? users : []).find(u => String(u.id) === String(viewRootTask.assignedTo));
                  const avatar = assignee?.avatar || viewRootTask.assignedToAvatar || viewRootTask.authorAvatar;
                  return avatar
                    ? <img src={avatar} alt="avatar" style={{width:40,height:40,borderRadius:8,objectFit:'cover'}} />
                    : <FaUserCircle size={40} color="#b2bec3" title="Аватар" />;
                } catch { return <FaUserCircle size={40} color="#b2bec3" title="Аватар" />; }
              })()}
              <span style={{fontWeight:600,fontSize:'1rem',color:'#6dd5ed'}}>
                Исполнитель (основная): {(() => { const assignee = (Array.isArray(users) ? users : []).find(u => String(u.id) === String(viewRootTask.assignedTo)); return getAssigneeLabel(assignee) || viewRootTask.assignedToUsername || '—'; })()}
              </span>
            </div>

            {parseCoAssigneeIdsFromTask(viewRootTask.coAssignees).length > 0 && (
              <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f8fafc', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#475569', marginBottom: 8 }}>Со-исполнители</div>
                <div style={{ fontSize: '0.95rem', color: '#1e293b', lineHeight: 1.5 }}>
                  {parseCoAssigneeIdsFromTask(viewRootTask.coAssignees).map((cid) => {
                    const u = (Array.isArray(users) ? users : []).find(x => String(x.id) === String(cid));
                    return getAssigneeLabel(u) || `id ${cid}`;
                  }).join(' · ')}
                </div>
              </div>
            )}

            {(() => {
              try {
                let files = [];
                if (viewRootTask.file_info) {
                  files = JSON.parse(viewRootTask.file_info);
                } else if (viewRootTask.files) {
                  files = Array.isArray(viewRootTask.files) ? viewRootTask.files : JSON.parse(viewRootTask.files);
                } else if (viewRootTask.attachments) {
                  files = Array.isArray(viewRootTask.attachments) ? viewRootTask.attachments : JSON.parse(viewRootTask.attachments);
                }
                if (!Array.isArray(files) || files.length === 0) {
                  return (
                    <div style={{marginBottom:20,padding:'12px 16px',background:'#f0f4f8',borderRadius:8}}>
                      <span style={{color:'#b2bec3',fontSize:'0.9rem'}}>Файлы не прикреплены</span>
                    </div>
                  );
                }
                return (
                  <div style={{marginBottom:20}}>
                    <h4 style={{color:'#6dd5ed',fontSize:'1rem',fontWeight:600,marginBottom:12}}>Прикрепленные файлы</h4>
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {files.map((f, idx) => {
                        const fileName = f.originalname || f.name || f.filename || 'file';
                        const sizeKb = f.size ? (f.size/1024).toFixed(1) : '';
                        const ext = (f.originalname || f.name || '').split('.').pop()?.toLowerCase();
                        return (
                          <div key={idx} style={{background:'#e2e8f0',borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                            <div style={{display:'flex',alignItems:'center',gap:12,minWidth:0,flex:1}}>
                              <img
                                src={getFileIcon(fileName)}
                                alt="file icon"
                                style={{width:32,height:32,borderRadius:6,objectFit:'contain'}}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'inline-flex';
                                }}
                              />
                              <span style={{width:32,height:32,borderRadius:6,display:'none',alignItems:'center',justifyContent:'center',background:'rgba(109, 213, 237, 0.2)',color:'#6dd5ed',fontWeight:600,fontSize:'0.7rem'}}>
                                {(ext||'?').slice(0,3).toUpperCase()}
                              </span>
                              <div style={{minWidth:0,flex:1}}>
                                <div style={{color:'#0f172a',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                  {decodeURIComponent(fileName)}
                                </div>
                                {sizeKb && <div style={{color:'#64748b',fontSize:'0.8rem'}}>{sizeKb} КБ</div>}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleFileDownload(f.filename, fileName)}
                              style={{
                                padding:'6px 12px',
                                background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color:'#fff',
                                border:'none',
                                borderRadius:6,
                                fontSize:'0.8rem',
                                fontWeight:600,
                                cursor:'pointer'
                              }}
                            >
                              Скачать
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              } catch { return null; }
            })()}

            <div style={{marginBottom:20,padding:'12px 16px',background:'#f0f4f8',borderRadius:12}}>
              <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                <span style={{fontSize:'1rem',color:'#0f172a',fontWeight:600}}>
                  🕒 Дедлайн (основная): {modalDeadlineStr}
                </span>
                {deadlineTimer && (
                  <span style={{padding:'4px 8px',borderRadius:6,background: deadlineTimer==='просрочено' ? '#e74c3c' : '#f39c12',color:'#fff',fontSize:'0.8rem',fontWeight:600}}>
                    {deadlineTimer === 'просрочено' ? '⏰ Просрочено' : deadlineTimer}
                  </span>
                )}
              </div>
            </div>

            <h4 style={{color:'#6dd5ed',fontSize:'1.05rem',fontWeight:700,margin:'0 0 12px 0'}}>Подзадачи</h4>
            <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:22}}>
              {subtasksOfViewRoot.length === 0 && (
                <div style={{padding:12,background:'#f1f5f9',borderRadius:8,color:'#64748b',fontSize:'0.9rem'}}>Пока нет подзадач</div>
              )}
              {subtasksOfViewRoot.map(st => {
                const stAssignee = (Array.isArray(users) ? users : []).find(u => String(u.id) === String(st.assignedTo));
                const done = st.status === 'completed' || st.status === 'closed';
                const canAct = userParticipatesInTaskTree(user?.id, viewRootTask.id, tasks);
                return (
                  <div
                    key={st.id}
                    style={{
                      border:'1px solid #cbd5e1',
                      borderRadius:10,
                      padding:12,
                      background:'#fff',
                      display:'flex',
                      flexDirection:'column',
                      gap:8
                    }}
                  >
                    <div style={{fontWeight:700,color:'#0f172a'}}>{st.title}</div>
                    {st.description ? (
                      <div style={{fontSize:'0.88rem',color:'#475569',whiteSpace:'pre-line'}}>{st.description}</div>
                    ) : null}
                    <div style={{fontSize:'0.85rem',color:'#64748b'}}>
                      Исполнитель: {getAssigneeLabel(stAssignee) || st.assignedToUsername || '—'} · срок:{' '}
                      {st.deadline ? new Date(st.deadline).toLocaleString('ru-RU', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                    </div>
                    <div style={{fontSize:'0.8rem',fontWeight:600,color: done ? '#16a34a' : '#7c3aed'}}>
                      {done ? '✔ Выполнена' : '⏳ Открыта'}
                      {done && st.completionComment ? ` — ${st.completionComment}` : ''}
                    </div>
                    {!done && canAct && (
                      <button
                        type="button"
                        style={{
                          alignSelf:'flex-start',
                          padding:'8px 14px',
                          background:'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                          color:'#fff',
                          border:'none',
                          borderRadius:8,
                          cursor:'pointer',
                          fontWeight:600,
                          fontSize:'0.85rem'
                        }}
                        onClick={() => setShowComplete(st.id)}
                      >
                        Закрыть подзадачу
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {viewRootTask.status !== 'completed' && viewRootTask.status !== 'closed' &&
              userParticipatesInTaskTree(user?.id, viewRootTask.id, tasks) && (
              <form onSubmit={handleAddSubtask} style={{marginBottom:24,padding:16,background:'#f0f9ff',borderRadius:12,border:'1px solid #bae6fd'}}>
                <div style={{fontWeight:700,marginBottom:10,color:'#0369a1'}}>Новая подзадача</div>
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  <input
                    placeholder="Заголовок подзадачи"
                    value={subtaskForm.title}
                    onChange={e => setSubtaskForm(f => ({ ...f, title: e.target.value }))}
                    required
                    style={{padding:10,borderRadius:8,border:'1px solid #cbd5e1',fontSize:'0.95rem'}}
                  />
                  <textarea
                    placeholder="Описание (необязательно)"
                    value={subtaskForm.description}
                    onChange={e => setSubtaskForm(f => ({ ...f, description: e.target.value }))}
                    rows={2}
                    style={{padding:10,borderRadius:8,border:'1px solid #cbd5e1',fontSize:'0.9rem',resize:'vertical'}}
                  />
                  <div style={{position:'relative',maxWidth:isMobile ? '100%' : 400,width:'100%'}}>
                    <input
                      placeholder="Исполнитель подзадачи"
                      value={subtaskAssigneeQuery}
                      onChange={e => { setSubtaskAssigneeQuery(e.target.value); setSubtaskAssigneeOpen(true); setSubtaskAssigneeHighlight(0); }}
                      onFocus={() => setSubtaskAssigneeOpen(true)}
                      onBlur={() => setTimeout(() => setSubtaskAssigneeOpen(false), 150)}
                      onKeyDown={(e) => {
                        const len = (filteredSubtaskAssignees || []).length;
                        if (!len) return;
                        if (e.key === 'ArrowDown') { e.preventDefault(); setSubtaskAssigneeHighlight(h => Math.min(h + 1, len - 1)); }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setSubtaskAssigneeHighlight(h => Math.max(h - 1, 0)); }
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const u = filteredSubtaskAssignees[subtaskAssigneeHighlight];
                          if (u) {
                            setSubtaskForm(f => ({ ...f, assignedTo: u.id }));
                            setSubtaskAssigneeQuery(getAssigneeLabel(u));
                            setSubtaskAssigneeOpen(false);
                          }
                        }
                        if (e.key === 'Escape') setSubtaskAssigneeOpen(false);
                      }}
                      style={{width:'100%',padding:10,borderRadius:8,border:'1px solid #cbd5e1',fontSize:'0.95rem',boxSizing:'border-box'}}
                    />
                    {subtaskAssigneeOpen && (
                      <ul style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #cbd5e1',borderRadius:8,margin:4,listStyle:'none',padding:6,maxHeight:200,overflowY:'auto',zIndex:10,boxShadow:'0 8px 24px rgba(0,0,0,0.12)'}}>
                        {(filteredSubtaskAssignees || []).map((u, idx) => (
                          <li
                            key={u.id}
                            onMouseEnter={() => setSubtaskAssigneeHighlight(idx)}
                            onMouseDown={ev => {
                              ev.preventDefault();
                              setSubtaskForm(f => ({ ...f, assignedTo: u.id }));
                              setSubtaskAssigneeQuery(getAssigneeLabel(u));
                              setSubtaskAssigneeOpen(false);
                            }}
                            style={{
                              padding:'8px 10px',
                              borderRadius:6,
                              cursor:'pointer',
                              background: idx === subtaskAssigneeHighlight ? '#e0f2fe' : 'transparent',
                              color:'#0f172a',
                              fontSize:'0.9rem'
                            }}
                          >
                            {getAssigneeLabel(u)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:10,alignItems:'center'}}>
                    <input
                      type="datetime-local"
                      value={subtaskForm.deadline}
                      onChange={e => setSubtaskForm(f => ({ ...f, deadline: e.target.value }))}
                      required
                      style={{padding:10,borderRadius:8,border:'1px solid #cbd5e1'}}
                    />
                    <select
                      value={subtaskForm.priority}
                      onChange={e => setSubtaskForm(f => ({ ...f, priority: e.target.value }))}
                      style={{padding:10,borderRadius:8,border:'1px solid #cbd5e1'}}
                    >
                      <option value="low">Низкий</option>
                      <option value="medium">Средний</option>
                      <option value="high">Высокий</option>
                    </select>
                    <button
                      type="submit"
                      style={{
                        padding:'10px 18px',
                        background:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                        color:'#fff',
                        border:'none',
                        borderRadius:8,
                        fontWeight:700,
                        cursor:'pointer'
                      }}
                    >
                      Добавить подзадачу
                    </button>
                  </div>
                </div>
              </form>
            )}

            <h4 style={{color:'#6dd5ed',fontSize:'1.05rem',fontWeight:700,margin:'0 0 10px 0'}}>История</h4>
            <div style={{marginBottom:20,maxHeight:240,overflowY:'auto',padding:12,background:'#f8fafc',borderRadius:10,border:'1px solid #e2e8f0',fontSize:'0.88rem',lineHeight:1.45,color:'#334155'}}>
              {taskDetailTimeline.length === 0 && <span style={{color:'#94a3b8'}}>Событий пока нет</span>}
              {taskDetailTimeline.map((e, i) => {
                const whoUser = e.userId != null ? users.find(u => String(u.id) === String(e.userId)) : null;
                const who = getAssigneeLabel(whoUser) || (e.userId != null ? `участник #${e.userId}` : 'Система');
                const d = e.ts ? new Date(e.ts).toLocaleString('ru-RU') : '';
                let line = '';
                if (e.type === 'task_created') line = `${who}: создана задача «${e.title || ''}»`;
                else if (e.type === 'subtask_created') line = `${who}: добавлена подзадача «${e.title || ''}»`;
                else if (e.type === 'completed') line = `${who}: ${e.isSubtask ? 'подзадача' : 'основная задача'} «${e.title || ''}» закрыта${e.comment ? ` — ${e.comment}` : ''}`;
                else if (e.type === 'deadline_extended') line = `${who}: сдвинут срок по «${e.title || ''}»`;
                else if (e.type === 'note') line = `${who}: заметка — ${e.text || ''}`;
                else line = `${who}: ${e.type || 'событие'}`;
                return <div key={i} style={{marginBottom:8}}><span style={{color:'#64748b'}}>[{d}]</span> {line}</div>;
              })}
            </div>

            {userParticipatesInTaskTree(user?.id, viewRootTask.id, tasks) && (
              <div style={{marginBottom:20,padding:14,background:'#fefce8',borderRadius:10,border:'1px solid #fde047'}}>
                <div style={{fontWeight:600,marginBottom:8,color:'#854d0e'}}>Заметка в историю</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:8,alignItems:'flex-end'}}>
                  <textarea
                    value={timelineNote}
                    onChange={e => setTimelineNote(e.target.value)}
                    placeholder="Короткий комментарий для ленты…"
                    rows={2}
                    style={{flex:1,minWidth:isMobile ? 0 : 200,padding:10,borderRadius:8,border:'1px solid #eab308',fontSize:'0.9rem'}}
                  />
                  <button
                    type="button"
                    onClick={handleSaveTimelineNote}
                    style={{padding:'10px 16px',background:'#ca8a04',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:'pointer'}}
                  >
                    Сохранить
                  </button>
                </div>
              </div>
            )}

            <div style={{display:'flex',gap:12,marginTop:12,flexWrap:'wrap'}}>
              {viewRootTask.status !== 'completed' && viewRootTask.status !== 'closed' &&
                userParticipatesInTaskTree(user?.id, viewRootTask.id, tasks) && (
                <button
                  type="button"
                  style={{
                    padding:'10px 16px',
                    background:'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    color:'#fff',
                    border:'none',
                    borderRadius:8,
                    cursor:'pointer',
                    fontWeight:600,
                    fontSize:'0.9rem'
                  }}
                  onClick={() => {
                    setIsCompletionRequired(false);
                    setShowComplete(viewRootTask.id);
                    setViewTaskId(null);
                  }}
                >
                  Закрыть основную задачу
                </button>
              )}
              {userParticipatesInTaskTree(user?.id, viewRootTask.id, tasks) && (
                <button
                  type="button"
                  style={{
                    padding:'10px 16px',
                    background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color:'#fff',
                    border:'none',
                    borderRadius:8,
                    cursor:'pointer',
                    fontWeight:600,
                    fontSize:'0.9rem'
                  }}
                  onClick={() => { setShowExtend(viewRootTask.id); setViewTaskId(null); }}
                >
                  Сдвинуть дедлайн (основная)
                </button>
              )}
              {state.user?.role === 'admin' && (
                <button
                  type="button"
                  style={{
                    padding:'10px 16px',
                    background:'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color:'#fff',
                    border:'none',
                    borderRadius:8,
                    cursor:'pointer',
                    fontWeight:600,
                    fontSize:'0.9rem'
                  }}
                  onClick={() => { handleDelete(viewRootTask.id); setViewTaskId(null); }}
                >
                  Удалить задачу целиком
                </button>
              )}
            </div>
          </div>
        </ModalBg>
      )}

      {showComment && (
        <ModalBg onClose={()=>setShowComment(null)}>
          <div style={{maxWidth:'480px',minWidth:'300px',background:'rgba(17,24,39,0.75)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'16px',boxShadow:'0 20px 60px rgba(2,6,23,0.45)',padding:'28px 22px 22px 22px',margin:'60px auto',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',position:'relative',zIndex:10000}}>
            <h3 style={{color:'#e2e8f0',marginBottom:16,fontSize:'1.12em',textAlign:'center'}}>Комментарий к задаче</h3>
            <div style={{fontSize:'1.02em',color:'#cbd5e1',whiteSpace:'pre-line',maxHeight:'180px',overflowY:'auto',textAlign:'center',wordBreak:'break-word',padding:'8px 0'}}>
              {tasks.find(tt => String(tt.id) === String(showComment))?.completionComment || 'Комментарий отсутствует'}
            </div>
          </div>
        </ModalBg>
      )}

      {showSearchModal && !viewTaskId && (
        <ModalBg onClose={() => setShowSearchModal(false)}>
          <div
            style={{
              background:'rgba(44, 62, 80, 0.95)',
              border:'none',
              borderRadius:16,
              boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
              padding:'32px',
              color:'#fff',
              width:'min(1110px, 90vw)',
              maxHeight:'85vh',
              margin:'20px auto',
              position:'relative',
              overflow:'hidden',
              display:'flex',
              flexDirection:'column'
            }}
          >
            <h3 style={{color:'#6dd5ed',margin:'0 0 24px 0',fontWeight:800,fontSize:'1.8rem',textAlign:'left'}}>🔍 Поиск задач</h3>

            <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'center',marginBottom:24}}>
              <div style={{position:'relative', flex:'1', minWidth:280}}>
                <Input
                  placeholder={'Введите имя пользователя...'}
                  value={searchQuery}
                  onChange={e=>{ setSearchQuery(e.target.value); setSearchOpen(true); setSearchHighlight(0); }}
                  onFocus={()=> setSearchOpen(true)}
                  onBlur={()=> setTimeout(()=> setSearchOpen(false), 150)}
                  onKeyDown={(e)=>{
                    const len = (filteredSearchUsers||[]).length;
                    if(!len) return;
                    if(e.key==='ArrowDown'){ e.preventDefault(); setSearchHighlight(h=> Math.min(h+1, len-1)); }
                    if(e.key==='ArrowUp'){ e.preventDefault(); setSearchHighlight(h=> Math.max(h-1, 0)); }
                    if(e.key==='Enter'){
                      e.preventDefault();
                      const u = filteredSearchUsers[searchHighlight];
                      if(u){ setSearchUserId(String(u.id)); setSearchQuery(u.username || u.displayName || u.email || String(u.id)); setSearchDate(''); setSearchOpen(false); }
                    }
                    if(e.key==='Escape'){ setSearchOpen(false); }
                  }}
                  style={{height:48}}
                />
                {searchOpen && (
                  <ul style={{position:'absolute', top:52, left:0, right:0, background:'rgba(44, 62, 80, 0.98)', border:'1px solid rgba(109, 213, 237, 0.3)', borderRadius:12, boxShadow:'0 12px 28px rgba(0,0,0,0.4)', margin:0, padding:'8px', listStyle:'none', maxHeight:260, overflowY:'auto', zIndex:5}}>
                    {filteredSearchUsers && filteredSearchUsers.length === 0 && (
                      <li style={{padding:'12px 16px', color:'#b2bec3'}}>Ничего не найдено</li>
                    )}
                    {(filteredSearchUsers||[]).map((u, idx)=>{
                      const active = idx === searchHighlight;
                      const firstName = u.firstName || u.firstname || '';
                      const lastName = u.lastName || u.lastname || '';
                      const display = u.displayName || u.display || [firstName, lastName].filter(Boolean).join(' ').trim();
                      const login = u.login || u.username || '';
                      const label = (display || login || u.email || String(u.id)).trim();
                      return (
                        <li key={u.id}
                            onMouseEnter={()=>setSearchHighlight(idx)}
                            onMouseDown={(e)=>{ e.preventDefault(); setSearchUserId(String(u.id)); setSearchQuery(label); setSearchDate(''); setSearchOpen(false); }}
                            style={{padding:'12px 16px', borderRadius:8, cursor:'pointer', background: active? 'rgba(109, 213, 237, 0.2)' : 'transparent', color:'#fff', transition:'all 0.2s'}}>
                          {label}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <input 
                type="date" 
                value={searchDate} 
                onChange={e=>setSearchDate(e.target.value)} 
                style={{
                  height:48,
                  padding:'0 16px',
                  borderRadius:8,
                  border:'1px solid rgba(109, 213, 237, 0.3)',
                  background:'rgba(44, 62, 80, 0.5)',
                  color:'#fff',
                  fontSize:'0.9rem'
                }} 
              />
              <div style={{display:'inline-flex',border:'1px solid rgba(109, 213, 237, 0.3)',borderRadius:8,overflow:'hidden',height:48,alignItems:'stretch'}}>
                <button 
                  type="button" 
                  onClick={()=>setSearchRole('assignee')} 
                  style={{
                    padding:'0 16px',
                    fontWeight:600,
                    cursor:'pointer',
                    background: searchRole==='assignee' ? 'linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%)' : 'rgba(44, 62, 80, 0.5)', 
                    color: '#fff', 
                    border:'none',
                    fontSize:'0.9rem',
                    transition:'all 0.2s'
                  }}
                >
                  Исполнитель
                </button>
                <button 
                  type="button" 
                  onClick={()=>setSearchRole('author')} 
                  style={{
                    padding:'0 16px',
                    fontWeight:600,
                    cursor:'pointer',
                    background: searchRole==='author' ? 'linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%)' : 'rgba(44, 62, 80, 0.5)', 
                    color: '#fff', 
                    border:'none',
                    fontSize:'0.9rem',
                    transition:'all 0.2s'
                  }}
                >
                  Автор
                </button>
              </div>
              <button 
                onClick={()=>{setSearchUserId(''); setSearchQuery(''); setSearchDate('');}} 
                style={{
                  height:48,
                  padding:'0 16px',
                  borderRadius:8,
                  border:'1px solid rgba(109, 213, 237, 0.3)',
                  background:'rgba(44, 62, 80, 0.5)',
                  color:'#fff',
                  fontWeight:600,
                  cursor:'pointer',
                  fontSize:'0.9rem',
                  transition:'all 0.2s'
                }}
              >
                Сбросить
              </button>
            </div>

            <div style={{flex:1,overflowY:'auto',maxHeight:'75vh',paddingRight:'8px'}}>
              {searchResults.length === 0 ? (
                <div style={{
                  color:'#b2bec3',
                  fontWeight:600,
                  fontSize:'1.1rem',
                  textAlign:'center',
                  padding:'40px 20px',
                  background:'rgba(44, 62, 80, 0.3)',
                  borderRadius:8,
                  border:'1px solid rgba(255,255,255,0.08)'
                }}>
                  📭 Нет задач по выбранным параметрам
                </div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {searchResults.map(t => {
                    const authorUser = (Array.isArray(users)?users:[]).find(u => String(u.id) === String(t.authorId));
                    const priorityColor = t.priority === 'high' ? '#e74c3c' : t.priority === 'low' ? '#27ae60' : '#f39c12';
                    // Функция для очистки HTML-тегов
                    const stripHtmlTags = (html) => {
                      if (!html) return '';
                      const tmp = document.createElement('div');
                      tmp.innerHTML = html;
                      return tmp.textContent || tmp.innerText || '';
                    };
                    
                    // Очищаем HTML и получаем краткое описание: первые 3 слова
                    const cleanDescription = t.description ? stripHtmlTags(t.description) : '';
                    const shortDesc = cleanDescription ? cleanDescription.split(/\s+/).slice(0,3).join(' ') + (cleanDescription.split(/\s+/).length > 3 ? '...' : '') : '';
                    
                    // Обработчики свайпа для мобильной версии в модалке поиска
                    const handleSearchTaskSwipeStart = (e) => {
                      if (!isMobile) return;
                      e.stopPropagation();
                      setSwipeData(prev => ({
                        ...prev,
                        [`search_${t.id}`]: { startX: e.touches[0].clientX }
                      }));
                    };

                    const handleSearchTaskSwipeMove = (e) => {
                      if (!isMobile || !swipeData[`search_${t.id}`]) return;
                      e.stopPropagation();
                    };

                    const handleSearchTaskSwipeEnd = (e) => {
                      if (!isMobile || !swipeData[`search_${t.id}`]) return;
                      e.stopPropagation();
                      const endX = e.changedTouches[0].clientX;
                      const startX = swipeData[`search_${t.id}`].startX;
                      const distance = endX - startX;
                      const minSwipeDistance = 50;

                      if (distance > minSwipeDistance) {
                        openTaskDetail(t);
                        setShowSearchModal(false);
                      }

                      setSwipeData(prev => {
                        const next = { ...prev };
                        delete next[`search_${t.id}`];
                        return next;
                      });
                    };
                    
                    return (
                      <div 
                        key={t.id} 
                        style={{
                          display:'flex',
                          flexDirection:'row',
                          alignItems:'center',
                          gap:8,
                          padding:'12px 16px',
                          background:'rgba(44, 62, 80, 0.3)',
                          borderRadius:8,
                          borderLeft: isMobile && !isLandscape ? `10px solid ${priorityColor}` : `3px solid ${priorityColor}`,
                          transition:'all 0.2s',
                          minHeight:80,
                          cursor: isMobile ? 'pointer' : 'default'
                        }}
                        onTouchStart={isMobile ? handleSearchTaskSwipeStart : undefined}
                        onTouchMove={isMobile ? handleSearchTaskSwipeMove : undefined}
                        onTouchEnd={isMobile ? handleSearchTaskSwipeEnd : undefined}
                      >
                        <div style={{display:'flex',alignItems:'center',gap:8,flex:1}}>
                          {authorUser && authorUser.avatar
                            ? <img src={authorUser.avatar} alt="avatar" style={{width:32,height:32,borderRadius:6,objectFit:'cover'}} />
                            : <FaUserCircle size={32} color="#b2bec3" title="Аватар" />}
                          <div style={{display:'flex',flexDirection:'column',gap:2,flex:1}}>
                            <span style={{fontSize:'0.7rem',color:'#000'}}>
                              Задача от {authorUser ? `${authorUser.firstName || ''} ${authorUser.lastName || ''}`.trim() || authorUser.username || 'Неизвестный' : 'Неизвестный'}
                            </span>
                            <span style={{fontSize:'0.95rem',fontWeight:600,lineHeight:1.2,color:'#fff'}}>{t.title}</span>
                            {(!isMobile || (isMobile && isLandscape)) && (
                              <span style={{fontSize:'0.8rem',color:'#b2bec3',lineHeight:1.2}}>{shortDesc}</span>
                            )}
                          </div>
                          {(!isMobile || (isMobile && isLandscape)) && (
                            <span style={{
                              padding:'4px 8px',
                              borderRadius:8,
                              background: t.priority === 'high' ? 'rgba(231, 76, 60, 0.2)' : 
                                         t.priority === 'low' ? 'rgba(39, 174, 96, 0.2)' : 'rgba(243, 156, 18, 0.2)',
                              color: priorityColor,
                              fontWeight:600,
                              fontSize:'0.8rem',
                              border:'1px solid rgba(255,255,255,0.08)',
                              display:'inline-flex',
                              alignItems:'center',
                              gap:'4px'
                            }}>
                              {t.priority === 'high' ? 'Высокий' :
                               t.priority === 'medium' ? 'Средний' : 'Низкий'}
                            </span>
                          )}
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap: isMobile && !isLandscape ? 6 : 8, flexWrap: isMobile && !isLandscape ? 'wrap' : 'nowrap'}}>
                          <span style={{
                            padding: isMobile && !isLandscape ? '2px 6px' : '4px 8px',
                            borderRadius:8,
                            background: 'var(--modal-tasks-deadline-chip-bg, rgba(248, 250, 252, 0.96))',
                            color: 'var(--modal-tasks-deadline-chip-text-color, #0f172a)',
                            fontWeight:600,
                            fontSize: isMobile && !isLandscape ? '0.75rem' : '0.8rem',
                            boxShadow:'0 1px 3px rgba(15, 23, 42, 0.08)',
                            border:'1px solid var(--modal-tasks-deadline-chip-border-color, rgba(148, 163, 184, 0.45))',
                            display:'inline-flex',
                            alignItems:'center',
                            gap:'6px',
                            position:'relative',
                            pointerEvents:'none'
                          }}>
                            🕒 {t.deadline ? new Date(t.deadline).toLocaleString('ru-RU', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                          </span>

                          {(t.status==='completed'||t.status==='closed') && t.completedAt ? (
                            <span style={{
                              padding: isMobile && !isLandscape ? '4px' : '4px 8px',
                              borderRadius:8,
                              background:'linear-gradient(90deg,#16a34a,#22c55e)',
                              color:'#eef2ff',
                              fontWeight:600,
                              fontSize: isMobile && !isLandscape ? '0.75rem' : '0.8rem',
                              boxShadow:'0 4px 11px rgba(99,102,241,0.35)',
                              border:'1px solid rgba(255,255,255,0.06)',
                              display:'inline-flex',
                              alignItems:'center',
                              gap: isMobile && !isLandscape ? 0 : '4px',
                              pointerEvents:'none',
                              minWidth: isMobile && !isLandscape ? 32 : undefined,
                              minHeight: isMobile && !isLandscape ? 32 : undefined,
                              justifyContent: 'center'
                            }}>
                              <span style={{fontSize:'0.8em'}}>✔️</span>
                              {(!isMobile || (isMobile && isLandscape)) && new Date(t.completedAt).toLocaleDateString('ru-RU')}
                            </span>
                          ) : (
                            <span style={{
                              padding: isMobile && !isLandscape ? '4px' : '4px 8px',
                              borderRadius:8,
                              background:'linear-gradient(90deg,#7c3aed,#3b82f6)',
                              color:'#eef2ff',
                              fontWeight:600,
                              fontSize: isMobile && !isLandscape ? '0.75rem' : '0.8rem',
                              boxShadow:'0 4px 11px rgba(99,102,241,0.35)',
                              border:'1px solid rgba(255,255,255,0.06)',
                              display:'inline-flex',
                              alignItems:'center',
                              gap: isMobile && !isLandscape ? 0 : '4px',
                              pointerEvents:'none',
                              minWidth: isMobile && !isLandscape ? 32 : undefined,
                              minHeight: isMobile && !isLandscape ? 32 : undefined,
                              justifyContent: 'center'
                            }}>
                              <span style={{fontSize:'0.8em'}}>⏳</span>
                              {(!isMobile || (isMobile && isLandscape)) && 'Открыта'}
                            </span>
                          )}
                          {isMobile && !isLandscape ? (
                            <button
                              type="button"
                              title="Открыть задачу"
                              onClick={(e)=>{ e.stopPropagation(); openTaskDetail(t); }}
                              style={{
                                width: 36,
                                height: 36,
                                minWidth: 36,
                                minHeight: 36,
                                padding: 0,
                                borderRadius: 8,
                                border: 'none',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#fff',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <FiChevronRight size={20} strokeWidth={2.5} />
                            </button>
                          ) : (
                            <div style={{display:'flex',gap:6}}>
                              <button 
                                type="button"
                                onClick={()=>{ openTaskDetail(t); }} 
                                style={{
                                  padding:'6px 12px',
                                  background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  color:'#fff',
                                  border:'none',
                                  borderRadius:8,
                                  cursor:'pointer',
                                  fontWeight:600,
                                  fontSize:'0.8rem',
                                  display:'flex',
                                  alignItems:'center',
                                  gap:'4px'
                                }}
                              >
                                Открыть
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </ModalBg>
      )}
    </div>
  );
}

export default Tasks;